import { translate } from "app/filters/i18nFilter";
import { LLMSetupFormStep3 } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/steps/LLMSetupFormStep3";
import { getCompetitorDomainErrorKeys } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/competitor-section/utils";
import { getCompetitorBrandNfkcError } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/brandValidation";
import { getDuplicateTrackedDomainIndices } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";

import { LLMSetupFormStep1 } from "./components/steps/LLMSetupFormStep1";
import { LLMSetupFormStep2 } from "./components/steps/LLMSetupFormStep2";
import { EMPTY_COMPETITOR } from "./LLMSetupConst";
import type { IStepConfig, IStepData, IStepError } from "./types";

export const STEP_CONFIGS: Record<number, IStepConfig> = {
    1: {
        validate: (data: IStepData): IStepError[] => {
            const errors: IStepError[] = [];

            if (!data.brandNames?.length) {
                errors.push({
                    field: "brand",
                    message: translate("llm.setup.error.empty-brand"),
                });
            } else {
                for (const brand of data.brandNames) {
                    const nfkcError = getCompetitorBrandNfkcError(brand);

                    if (nfkcError) {
                        errors.push({
                            field: "brand",
                            message: translate(nfkcError.key, nfkcError.params),
                        });
                        break;
                    }
                }
            }

            getDuplicateTrackedDomainIndices(data.domains).forEach((index) => {
                errors.push({
                    field: "domain",
                    index,
                    message: translate("llm.setup.error.duplicate-domain"),
                });
            });

            return errors;
        },
        component: LLMSetupFormStep1,
    },
    2: {
        validate: (data: IStepData): IStepError[] => {
            const errors: IStepError[] = [];

            //validate selected topics
            if (!data.selectedTopics?.length) {
                errors.push({
                    field: "topic",
                    message: translate("llm.setup.error.no-topics"),
                });
            }

            return errors;
        },
        component: LLMSetupFormStep2,
    },
    3: {
        validate: (data: IStepData): IStepError[] => {
            const errors: IStepError[] = [];

            //validate per-row competitor errors (only for partially-filled rows)
            data.competitors?.forEach((competitor, index) => {
                const hasDomain = !!competitor.domain;
                const hasBrands = !!competitor.brands?.length;

                if (hasDomain && !hasBrands) {
                    errors.push({
                        field: "competitor.brand",
                        index,
                        message: translate("llm.setup.error.competitor.brand.empty"),
                    });
                } else if (!hasDomain && hasBrands) {
                    errors.push({
                        field: "competitor.domain",
                        index,
                        message: translate("llm.setup.error.competitor.domain.empty"),
                    });
                }
            });

            getCompetitorDomainErrorKeys(data.competitors, data.domains).forEach((errorKey, index) => {
                errors.push({
                    field: "competitor.domain",
                    index,
                    message: translate(errorKey),
                });
            });

            return errors;
        },
        onStepChange: (data, { handleSetCompetitors }) => {
            const completeCompetitors =
                data?.competitors?.filter(({ domain, brands }) => domain && brands?.length) ?? [];

            const dedupedCompetitors = completeCompetitors.filter(
                (comp, idx) => completeCompetitors.findIndex((c) => c.domain?.name === comp.domain?.name) === idx,
            );

            handleSetCompetitors(dedupedCompetitors.length ? dedupedCompetitors : [EMPTY_COMPETITOR]);
        },
        component: LLMSetupFormStep3,
    },
};
